import * as changeCase from 'change-case';
import * as uuid from 'uuid';
import md5 from 'blueimp-md5';

export { changeCase, uuid, md5 };

/**
 * Add prefix for all object keys
 * Example:
 * renameKeys({ id: 1 }, 'some-prefix')
 * Result:
 * { 'someprefix-id': 1 }
 */
export const renameKeys = <T, K extends keyof T>(obj: T, prefix: string): T => {
	return Object.keys(obj as object).reduce(
		(memo, key) => ({ ...memo, [`${prefix}-${key}`]: obj[key as K] }),
		{} as T
	);
};

/**
 * Extending one nested object (IMap<some interface>) to another
 * Example:
 * extendObj({ id1: { key1: 0 } }, { addingKey: 1 }, 'id1' (or ['id1']))
 * Result:
 * {
 * 	id1: {
 * 		key1: 0,
 * 		addingKey: 1
 * 	}
 * }
 */
export const extendObj = <T, K extends keyof T>(obj: T, extObj: T, keysStr: string | string[]): T => {
	return Object.keys(obj as object)
		.filter((key) => keysStr.includes(key))
		.reduce((memo, key) => ({ ...memo, [key]: { ...memo[key as K], ...extObj } }), obj);
};

/**
 * Normalize data model
 * Example:
 * normalizeModel({ a: 0 }, ['a', 'b', 'c'])
 * Result:
 * {
 * 	a: 0,
 * 	b: null,
 * 	c: null
 * }
 */
export const normalizeModel = <T, K extends keyof T>(obj: T = {} as T, keys: string[]): T => {
	return keys.reduce((memo, key) => ({ ...memo, [key]: obj[key as K] || null }), obj);
};

/**
 * Equal two object by keys and values
 */
export const isEqual = <T, K extends keyof T>(object1: T, object2: T): boolean => {
	const props1 = Object.getOwnPropertyNames(object1).sort();
	const props2 = Object.getOwnPropertyNames(object2).sort();

	if (props1.toString() !== props2.toString()) {
		return false;
	}

	for (let i = 0; i < props1.length; i += 1) {
		const prop = props1[i];

		if (object1[prop as K] !== object2[prop as K]) {
			return false;
		}
	}

	return true;
};
